import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet, Upload, Loader2 } from "lucide-react";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";

const STATUS = {
  only_in_sap: { label: "Only in SAP extract", cls: "bg-rose-100 text-rose-700" },
  only_in_dashboard: { label: "Only in dashboard", cls: "bg-sky-100 text-sky-700" },
  amount_differs: { label: "Amount differs", cls: "bg-amber-100 text-amber-800" },
};

const TotalBox = ({ label, sap, dash, gap, id }) => (
  <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`gstr2-reco-total-${id}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-xs mt-1 tabular-nums">SAP: <b>{inr(sap || 0)}</b></div>
    <div className="text-xs tabular-nums">Dashboard: <b>{inr(dash || 0)}</b></div>
    <div className={`text-xs tabular-nums font-semibold ${Math.abs(gap) < 1 ? "text-emerald-700" : "text-rose-700"}`} data-testid={`gstr2-reco-gap-${id}`}>Gap: {inr(gap || 0)}</div>
  </div>
);

const RecoTable = ({ rows, onOpenDoc }) => (
  <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
    <Table data-testid="gstr2-reco-table">
      <TableHeader>
        <TableRow className="bg-slate-50">
          {["Document", "Posting", "Type", "Vendor / Bank", "Tax codes", "Side", "Status", "SAP tax", "Dashboard tax", "Difference", "Reason"].map((h) => (
            <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={`${r.doc}-${r.fiscal_year}-${r.side}`} className="hover:bg-sky-50/60 cursor-pointer" onClick={() => onOpenDoc({ accounting_document: r.doc, fiscal_year: r.fiscal_year, invoice_no: r.reference })} data-testid={`gstr2-reco-row-${r.doc}-${r.side}`}>
            <TableCell className="font-mono text-xs whitespace-nowrap">{r.doc}/{r.fiscal_year}<span className="block text-[10px] text-slate-400">{r.reference}</span></TableCell>
            <TableCell className="text-xs whitespace-nowrap">{r.posting_date}</TableCell>
            <TableCell className="text-xs">{r.doc_type}</TableCell>
            <TableCell className="text-xs max-w-[180px] truncate" title={r.vendor}>{r.vendor || "—"}</TableCell>
            <TableCell className="text-xs">{r.tax_codes || "—"}</TableCell>
            <TableCell className="text-xs capitalize">{r.side}</TableCell>
            <TableCell><Badge className={`${STATUS[r.status].cls} text-[10px] whitespace-nowrap`}>{STATUS[r.status].label}</Badge></TableCell>
            <TableCell className="text-xs text-right tabular-nums whitespace-nowrap">{inr(r.sap_tax)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums whitespace-nowrap">{inr(r.dashboard_tax)}</TableCell>
            <TableCell className="text-xs text-right tabular-nums font-semibold whitespace-nowrap">{inr(r.difference)}</TableCell>
            <TableCell className="text-[11px] text-slate-600 min-w-[260px]">{r.reason}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const GSTR2SapReconcile = ({ onOpenDoc }) => {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [status, setStatus] = useState("all");
  const rows = useMemo(() => (res?.rows || []).filter((r) => status === "all" || r.status === status), [res, status]);

  const run = () => {
    if (!files.length) return toast.error("Choose the SAP TAXCODE and/or BANK CHARGES .xlsx first");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    axios.post(`${API}/reports/gstr2/reconcile-extract`, fd)
      .then((r) => { setRes(r.data); setStatus("all"); })
      .catch((e) => toast.error(e.response?.data?.detail || "Reconciliation failed"))
      .finally(() => setBusy(false));
  };

  const t = res?.totals || {};
  return (
    <Card className="border-slate-200 shadow-sm fade-in-up" data-testid="gstr2-sap-reco">
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500 flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Reconcile with SAP extract</p>
            <p className="text-[11px] text-slate-500 max-w-2xl mt-1">Upload SAP's GSTR2 TAXCODE and/or GSTR2 BANK CHARGES query export (.xlsx, as downloaded). The period is read from the extract; documents are matched on accounting document + fiscal year.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-xs text-slate-600 cursor-pointer hover:border-sky-400" data-testid="gstr2-reco-file-label">
              <Upload className="h-3.5 w-3.5" /> {files.length ? files.map((f) => f.name).join(", ") : "Choose up to 2 files"}
              <input type="file" accept=".xlsx" multiple className="hidden" data-testid="gstr2-reco-file-input" onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 2))} />
            </label>
            <Button size="sm" onClick={run} disabled={busy} data-testid="gstr2-reco-run-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reconcile"}</Button>
          </div>
        </div>
        {res && (
          <div className="space-y-3" data-testid="gstr2-reco-result">
            <p className="text-[11px] text-slate-500" data-testid="gstr2-reco-period">Period {res.period.from} → {res.period.to} · {res.files.map((f) => `${f.kind.toUpperCase()}: ${f.documents} docs`).join(" · ")} · dashboard register built {(res.register_built_at || "").slice(0, 16).replace("T", " ") || "—"}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TotalBox id="eligible" label="Eligible ITC (incl. bank charges)" sap={t.sap_eligible} dash={t.dash_eligible} gap={t.eligible_gap} />
              <TotalBox id="ineligible" label="Ineligible / blocked ITC" sap={t.sap_ineligible} dash={t.dash_ineligible} gap={t.ineligible_gap} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[["all", `All differences (${res.rows.length})`], ["match", `Matched: ${res.by_status.match.count}`], ...Object.keys(STATUS).map((k) => [k, `${STATUS[k].label}: ${res.by_status[k].count} · ${inr(res.by_status[k].amount)}`])].map(([k, l]) => (
                <button type="button" key={k} disabled={k === "match"} onClick={() => setStatus(k)} data-testid={`gstr2-reco-filter-${k}`}
                  className={`rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${status === k ? "border-sky-400 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-700 hover:border-sky-300"} ${k === "match" ? "cursor-default" : ""}`}>{l}</button>
              ))}
            </div>
            {rows.length ? <RecoTable rows={rows} onOpenDoc={onOpenDoc} /> : <p className="text-xs text-emerald-700" data-testid="gstr2-reco-clean">No differences — every document ties.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
